import type React from 'react'
import { useCallback, useMemo, useRef } from 'react'
import {
  useBoolean,
  useCallbackRef,
  useControllableState,
  useDimensions,
  useIds,
  useLatestRef,
  usePanGesture,
  useUpdateEffect
} from '@chakra-ui/hooks'
import type { EventKeyMap, PropGetter } from '@chakra-ui/react-utils'
import { mergeRefs } from '@chakra-ui/react-utils'
import type { AnyPointerEvent } from '@chakra-ui/utils'
import {
  ariaAttr,
  callAllHandlers,
  clampValue,
  dataAttr,
  focus,
  getBox,
  normalizeEventKey,
  percentToValue,
  roundValueToStep,
  valueToPercent
} from '@chakra-ui/utils'
import type { HTMLXStyledProps } from '../../system'
import type { Direction, Orientation, SliderSize } from './types'
import { getDefaultValue, getIsReversed, getStyles, orient } from './utils'

export interface UseSliderProps {
  min?: number
  max?: number
  step?: number
  value?: number
  defaultValue?: number
  orientation?: Orientation
  isReversed?: boolean
  onChangeStart?: (value: number) => void
  onChangeEnd?: (value: number) => void
  onChange?: (value: number) => void
  id?: string
  name?: string
  isDisabled?: boolean
  isReadOnly?: boolean
  getAriaValueText?: (value: number) => string
  focusThumbOnChange?: boolean
  'aria-valuetext'?: string
  'aria-label'?: string
  'aria-labelledby'?: string
  direction?: Direction
  size?: SliderSize
}

/**
 * Chakra UI React hook that implements an accessible range slider.
 * Modified and converted to plain Javascript.
 *
 * It is an alternative to `<input type="range" />`, and returns
 * prop getters for the component parts
 *
 * @see Docs     https://chakra-ui.com/docs/form/slider
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/main/packages/slider/src/use-slider.ts
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.1/#slider
 */
export function useSlider(props: UseSliderProps) {
  const {
    min = 0,
    max = 100,
    onChange,
    value: valueProp,
    defaultValue,
    isReversed: isReversedProp,
    direction = 'ltr',
    orientation = 'horizontal',
    size: sizeProp = 'md',
    id: idProp,
    isDisabled,
    isReadOnly,
    onChangeStart: onChangeStartProp,
    onChangeEnd: onChangeEndProp,
    step,
    getAriaValueText: getAriaValueTextProp,
    'aria-valuetext': ariaValueText,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    name,
    focusThumbOnChange = true,
    ...htmlProps
  } = props

  const onChangeStart = useCallbackRef(onChangeStartProp)
  const onChangeEnd = useCallbackRef(onChangeEndProp)
  const getAriaValueText = useCallbackRef(getAriaValueTextProp)

  const isReversed = getIsReversed({
    isReversed: isReversedProp,
    direction,
    orientation
  })

  const [computedValue, setValue] = useControllableState({
    value: valueProp,
    defaultValue: defaultValue ?? getDefaultValue(min, max),
    onChange
  })

  const [isDragging, setDragging] = useBoolean()
  const [isFocused, setFocused] = useBoolean()
  const eventSourceRef = useRef<'pointer' | 'keyboard' | null>(null)

  const isInteractive = !(isDisabled || isReadOnly)
  const isVertical = orientation === 'vertical'

  const value = clampValue(computedValue, min, max)
  const valueRef = useLatestRef(value)
  const prevRef = useRef(valueRef.current)
  const trackRef = useRef<HTMLElement>(null)
  const thumbRef = useRef<HTMLElement>(null)
  const rootRef = useRef<HTMLElement>(null)

  const reversedValue = max - value + min
  const trackValue = isReversed ? reversedValue : value
  const thumbPercent = valueToPercent(trackValue, min, max)

  const [thumbId, trackId] = useIds(idProp, `slider-thumb`, `slider-track`)

  const getValueFromPointer = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (!trackRef.current) return
      eventSourceRef.current = 'pointer'
      const trackRect = getBox(trackRef.current).borderBox
      const { clientX, clientY } = (event as TouchEvent).touches?.[0] ?? event

      const diff = isVertical
        ? trackRect.bottom - clientY
        : clientX - trackRect.left

      const length = isVertical ? trackRect.height : trackRect.width
      let percent = diff / length

      if (isReversed) {
        percent = 1 - percent
      }

      let nextValue = percentToValue(percent, min, max)

      if (step) {
        nextValue = parseFloat(roundValueToStep(nextValue, min, step))
      }

      nextValue = clampValue(nextValue, min, max)

      return nextValue
    },
    [isVertical, isReversed, max, min, step]
  )

  const tenSteps = (max - min) / 10
  const oneStep = step || (max - min) / 100

  const constrain = useCallback(
    (value: number) => {
      if (!isInteractive) return
      value = parseFloat(roundValueToStep(value, min, oneStep))
      value = clampValue(value, min, max)
      setValue(value)
    },
    [oneStep, max, min, setValue, isInteractive]
  )

  const actions = useMemo(
    () => ({
      stepUp: (step = oneStep) => {
        const next = isReversed ? value - step : value + step
        constrain(next)
      },
      stepDown: (step = oneStep) => {
        const next = isReversed ? value + step : value - step
        constrain(next)
      },
      reset: () => constrain(defaultValue || 0),
      stepTo: (value: number) => constrain(value)
    }),
    [constrain, isReversed, value, oneStep, defaultValue]
  )

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const eventKey = normalizeEventKey(event)
      const keyMap: EventKeyMap = {
        ArrowRight: () => actions.stepUp(),
        ArrowUp: () => actions.stepUp(),
        ArrowLeft: () => actions.stepDown(),
        ArrowDown: () => actions.stepDown(),
        PageUp: () => actions.stepUp(tenSteps),
        PageDown: () => actions.stepDown(tenSteps),
        Home: () => constrain(min),
        End: () => constrain(max)
      }

      const action = keyMap[eventKey]

      if (action) {
        event.preventDefault()
        event.stopPropagation()
        action(event)
        eventSourceRef.current = 'keyboard'
      }
    },
    [actions, constrain, max, min, tenSteps]
  )

  const valueText = getAriaValueText?.(value) ?? ariaValueText
  const thumbBoxModel = useDimensions(thumbRef)

  const { getThumbStyle, rootStyle, trackStyle, innerTrackStyle } =
    useMemo(() => {
      const thumbRect = thumbBoxModel?.borderBox ?? { width: 0, height: 0 }
      return getStyles({
        isReversed,
        orientation,
        thumbRects: [thumbRect],
        thumbPercents: [thumbPercent]
      })
    }, [isReversed, orientation, thumbBoxModel?.borderBox, thumbPercent])

  const focusThumb = useCallback(() => {
    if (thumbRef.current && focusThumbOnChange) {
      setTimeout(() => focus(thumbRef.current))
    }
  }, [focusThumbOnChange])

  useUpdateEffect(() => {
    focusThumb()
    if (eventSourceRef.current === 'keyboard') {
      onChangeEnd?.(valueRef.current)
    }
  }, [value, onChangeEnd])

  const setValueFromPointer = (event: AnyPointerEvent) => {
    const nextValue = getValueFromPointer(event)
    if (nextValue != null && nextValue !== valueRef.current) {
      setValue(nextValue)
    }
  }

  usePanGesture(rootRef, {
    onPanSessionStart(event) {
      if (!isInteractive) return
      setDragging.on()
      focusThumb()
      setValueFromPointer(event)
      onChangeStart?.(valueRef.current)
    },
    onPanSessionEnd() {
      if (!isInteractive) return
      setDragging.off()
      onChangeEnd?.(valueRef.current)
      prevRef.current = valueRef.current
    },
    onPan(event) {
      if (!isInteractive) return
      setValueFromPointer(event)
    }
  })

  const getRootProps = useCallback<
    PropGetter<
      HTMLDivElement,
      HTMLXStyledProps<'div'> & {
        orientation?: Orientation
      }
    >
  >(
    (props = {}, ref = null) => ({
      ...props,
      ...htmlProps,
      orientation,
      ref: mergeRefs(ref, rootRef),
      tabIndex: -1,
      'data-orientation': orientation,
      'data-disabled': dataAttr(isDisabled),
      'aria-disabled': ariaAttr(isDisabled),
      'data-focused': dataAttr(isFocused),
      style: {
        ...props.style,
        ...rootStyle
      }
    }),
    [htmlProps, isDisabled, isFocused, orientation, rootStyle]
  )

  const getTrackProps = useCallback<
    PropGetter<
      HTMLDivElement,
      HTMLXStyledProps<'div'> & {
        size?: SliderSize
        orientation?: Orientation
      }
    >
  >(
    (props = {}, ref = null) => ({
      ...props,
      orientation,
      size: sizeProp,
      ref: mergeRefs(ref, trackRef),
      id: trackId,
      'data-orientation': orientation,
      'data-disabled': dataAttr(isDisabled),
      style: {
        ...props.style,
        ...trackStyle
      }
    }),
    [isDisabled, trackId, sizeProp, orientation, trackStyle]
  )

  const getInnerTrackProps = useCallback<
    PropGetter<HTMLDivElement, HTMLXStyledProps<'div'>>
  >(
    (props = {}, ref = null) => ({
      ...props,
      ref,
      style: {
        ...props.style,
        ...innerTrackStyle
      }
    }),
    [innerTrackStyle]
  )

  const getThumbProps = useCallback<
    PropGetter<
      HTMLDivElement,
      HTMLXStyledProps<'div'> & {
        size?: SliderSize
        orientation?: Orientation
      }
    >
  >(
    (props = {}, ref = null) => ({
      ...props,
      orientation,
      size: sizeProp,
      ref: mergeRefs(ref, thumbRef),
      role: 'slider',
      tabIndex: isInteractive ? 0 : undefined,
      id: thumbId,
      'data-active': dataAttr(isDragging),
      'data-disabled': dataAttr(isDisabled),
      'aria-valuetext': valueText,
      'aria-valuemin': min,
      'aria-valuemax': max,
      'aria-valuenow': value,
      'aria-orientation': orientation,
      'aria-disabled': ariaAttr(isDisabled),
      'aria-readonly': ariaAttr(isReadOnly),
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabel ? undefined : ariaLabelledBy,
      style: {
        ...props.style,
        ...getThumbStyle(0)
      },
      onKeyDown: callAllHandlers(props.onKeyDown, onKeyDown),
      onFocus: callAllHandlers(props.onFocus, setFocused.on),
      onBlur: callAllHandlers(props.onBlur, setFocused.off)
    }),
    [
      isInteractive,
      thumbId,
      isDragging,
      valueText,
      orientation,
      sizeProp,
      min,
      max,
      value,
      orientation,
      isDisabled,
      isReadOnly,
      ariaLabel,
      ariaLabelledBy,
      getThumbStyle,
      onKeyDown,
      setFocused.on,
      setFocused.off
    ]
  )

  const getMarkerProps = useCallback<
    PropGetter<HTMLDivElement, HTMLXStyledProps<'div'> & { value?: any }>
  >(
    (props = {}, ref = null) => {
      const isInRange = !(props.value < min || props.value > max)
      const isHighlighted = value >= props.value
      const markerPercent = valueToPercent(props.value, min, max)

      const markerStyle: React.CSSProperties = {
        position: 'absolute',
        pointerEvents: 'none',
        ...orient({
          orientation,
          vertical: {
            bottom: isReversed ? `${100 - markerPercent}%` : `${markerPercent}%`
          },
          horizontal: {
            left: isReversed ? `${100 - markerPercent}%` : `${markerPercent}%`
          }
        })
      }

      return {
        ...props,
        ref,
        role: 'presentation',
        'aria-hidden': true,
        'data-disabled': dataAttr(isDisabled),
        'data-invalid': dataAttr(!isInRange),
        'data-highlighted': dataAttr(isHighlighted),
        style: {
          ...props.style,
          ...markerStyle
        }
      }
    },
    [isDisabled, isReversed, max, min, orientation, value]
  )

  const getInputProps: PropGetter<HTMLInputElement> = useCallback(
    (props = {}, ref = null) => ({
      ...props,
      ref,
      type: 'hidden',
      value,
      name
    }),
    [name, value]
  )

  return {
    state: {
      value,
      isFocused,
      isDragging
    },
    actions,
    getRootProps,
    getTrackProps,
    getInnerTrackProps,
    getThumbProps,
    getMarkerProps,
    getInputProps
  }
}

export type UseSliderReturn = ReturnType<typeof useSlider>
